import React from 'react'
import enhanceWithClickOutside from 'react-click-outside'
import { cn } from 'ethcss'
import { styles } from './Dropdown-styles'
import {
    IDropdownInputOption,
    IDropdownOption,
    IDropdownProps,
    IDropdownState,
    TDropdownIconSide,
    TDropdownMod,
    TDropdownOptionValue,
} from './Dropdown-types'
import { DropdownOption } from './DropdownOption'
import { DropdownLabel } from './DropdownLabel'
import Tippy from 'blocks.simple/tippy/tippy'
import translate from 'core/translate'

const DEFAULT_LABEL_KEY = 'label'
const DEFAULT_VALUE_KEY = 'value'

const getOptionField = (option: IDropdownInputOption, key: string): unknown => (option as Record<string, unknown>)[key]

const getOptionLabel = (option: IDropdownInputOption, key: string): string | null => {
    const label = getOptionField(option, key)

    if (typeof label === 'string') return label
    if (typeof label === 'number') return label.toString()

    return null
}

const getOptionValue = (option: IDropdownInputOption, key: string): TDropdownOptionValue | null => {
    const value = getOptionField(option, key)

    return typeof value === 'string' || typeof value === 'number' ? value : null
}

const getOptionIcon = (option: IDropdownInputOption): string | undefined => {
    const icon = getOptionField(option, 'icon')

    return typeof icon === 'string' ? icon : undefined
}

const getListFromOptions = <T extends IDropdownInputOption>(
    options: T[],
    labelKey: string = DEFAULT_LABEL_KEY,
    valueKey: string = DEFAULT_VALUE_KEY
): IDropdownOption[] => {
    return options.reduce<IDropdownOption[]>((list, option, sourceIndex) => {
        const label = getOptionLabel(option, labelKey)
        const value = getOptionValue(option, valueKey)

        if (label === null || value === null) return list

        list.push({ label, value, icon: getOptionIcon(option), sourceIndex })
        return list
    }, [])
}

class Dropdown<T extends IDropdownInputOption = IDropdownInputOption> extends React.PureComponent<
    IDropdownProps<T>,
    IDropdownState
> {
    private DEFAULT_MODE: TDropdownMod = 'withShadow'
    private DEFAULT_ICON_SIDE: TDropdownIconSide = 'left'

    constructor(props: IDropdownProps<T>) {
        super(props)

        this.state = {
            isActive: false,
        }
    }

    getLabel = (): string | null => {
        const { value, labelKey = DEFAULT_LABEL_KEY, valueKey = DEFAULT_VALUE_KEY, options } = this.props

        if (value === null) return null

        if (typeof value === 'string' || typeof value === 'number') {
            const option = options.find((item) => getOptionValue(item, valueKey) === value)
            return option ? getOptionLabel(option, labelKey) : null
        }

        return getOptionLabel(value, labelKey)
    }

    getValue = (): string | number | null => {
        const { value, valueKey = DEFAULT_VALUE_KEY, options } = this.props

        if (value === null) return null

        if (typeof value === 'string' || typeof value === 'number') {
            const option = options.find((item) => getOptionValue(item, valueKey) === value)
            return option ? getOptionValue(option, valueKey) : null
        }

        return getOptionValue(value, valueKey)
    }

    getWrapperClassName = () => {
        const { wrapperClassName, activeWrapperClassName, mod = this.DEFAULT_MODE } = this.props
        const { isActive } = this.state

        return cn(
            styles.Dropdown,
            styles[`Dropdown_type_${mod}`],
            isActive ? styles.Dropdown_type_active : {},
            wrapperClassName,
            isActive ? activeWrapperClassName : {}
        )
    }

    getListClassName = () => {
        const { listClassName } = this.props

        return cn(styles.Dropdown__list, listClassName)
    }

    toggleDropdown = () => {
        this.setState((prevState) => ({ ...prevState, isActive: !prevState.isActive }))
    }

    handleClickOutside() {
        const { isActive } = this.state

        if (isActive) this.toggleDropdown()
    }

    isEqualValues = (value: string | number, currentValue: string | number | null) => value === currentValue

    handleChangeOption = (label: string, value: string | number) => {
        if (this.isEqualValues(value, this.getValue())) return

        const { onChange, options, valueKey = DEFAULT_VALUE_KEY } = this.props
        const option = options.find((item) => getOptionValue(item, valueKey) === value)

        this.toggleDropdown()
        if (option) onChange(option)
    }

    renderList = () => {
        const {
            optionClassName,
            activeOptionClassName,
            optionIconColor,
            optionActiveIconColor,
            mod = this.DEFAULT_MODE,
            options,
            tippyDropdown,
            labelKey,
            valueKey,
        } = this.props
        const list = getListFromOptions(options, labelKey, valueKey)
        const currentLabel = this.getLabel()

        return (
            <div className={cn(this.getListClassName())}>
                {list.map(({ label, value, icon, sourceIndex }) => {
                    const isActive = currentLabel === label

                    return (
                        <React.Fragment key={value}>
                            {tippyDropdown ? (
                                <Tippy title={translate(`list__${getOptionField(options[sourceIndex], 'id')}`)}>
                                    <DropdownOption
                                        label={label}
                                        value={value}
                                        icon={icon}
                                        mod={mod}
                                        className={optionClassName}
                                        activeClassName={activeOptionClassName}
                                        onChangeOption={this.handleChangeOption}
                                        iconColor={optionIconColor}
                                        activeIconColor={optionActiveIconColor}
                                        isActive={isActive}
                                    />
                                </Tippy>
                            ) : (
                                <DropdownOption
                                    label={label}
                                    value={value}
                                    icon={icon}
                                    mod={mod}
                                    className={optionClassName}
                                    activeClassName={activeOptionClassName}
                                    onChangeOption={this.handleChangeOption}
                                    iconColor={optionIconColor}
                                    activeIconColor={optionActiveIconColor}
                                    isActive={isActive}
                                />
                            )}
                        </React.Fragment>
                    )
                })}
            </div>
        )
    }

    renderLabel = () => {
        const {
            label,
            icon,
            activeIcon = icon,
            iconSide = this.DEFAULT_ICON_SIDE,
            labelClassName,
            activeLabelClassName,
            labelIconColor,
            labelActiveIconColor,
            mod = this.DEFAULT_MODE,
            labelTippyTitle,
        } = this.props
        const { isActive } = this.state
        const dropdownLabel = this.getLabel() || label
        const currentIcon = isActive ? activeIcon : icon

        return (
            <Tippy title={labelTippyTitle}>
                <DropdownLabel
                    label={dropdownLabel}
                    icon={currentIcon}
                    mod={mod}
                    iconSide={iconSide}
                    onClick={this.toggleDropdown}
                    className={labelClassName}
                    activeClassName={activeLabelClassName}
                    iconColor={labelIconColor}
                    activeIconColor={labelActiveIconColor}
                    isActive={isActive}
                />
            </Tippy>
        )
    }

    render() {
        const { isActive } = this.state

        return (
            <div className={cn(this.getWrapperClassName())}>
                {this.renderLabel()}
                {isActive && this.renderList()}
            </div>
        )
    }
}

export default enhanceWithClickOutside(Dropdown)
